import { useState, useRef, useEffect, useCallback } from "react";
import { Bot, Send, Plus, Trash2, KeyRound, MessageSquare, ChevronDown, ChevronRight, Database, FileText, AlertTriangle } from "lucide-react";
import {
  getAssistantStatus, listAssistantConversations, getAssistantConversation,
  deleteAssistantConversation, deleteAllAssistantConversations, postAssistantChat,
} from "../lib/api";

// Asisten AI — a Gemini chat over the user's own Landhep data, every profile and
// every function (routers/chat.py). Conversations live in the DB; the user owns
// and deletes them. Each answer can show what the assistant read to produce it.

const STARTERS = [
  "Grid mana yang paling layak saya buka lebih dulu?",
  "Apa risiko terbesar di lokasi-lokasi saya?",
  "Bandingkan kompetisi di grid-grid yang saya miliki",
  "Berapa sisa saldo kredit saya dan dipakai untuk apa saja?",
];

const fmtTime = (iso) => {
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay ? d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("id-ID", { day: "numeric", month: "short" });
};

function AiAvatar() {
  return (
    <div className="w-7 h-7 rounded-lg bg-[#14543e] flex items-center justify-center shrink-0">
      <Bot className="w-3.5 h-3.5 text-white" />
    </div>
  );
}

// "Data yang dibaca": every tool call behind an answer — SQL + rows, or the report pulled.
function Trace({ meta }) {
  const [open, setOpen] = useState(false);
  const tools = meta?.tools || [];
  if (!tools.length) return null;
  const nErr = tools.filter((t) => t.error).length;
  return (
    <div className="mt-1.5">
      <button onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-400 hover:text-slate-600 transition-colors">
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        Data yang dibaca · {tools.length} langkah{nErr ? ` · ${nErr} gagal` : ""}{meta?.ms ? ` · ${(meta.ms / 1000).toFixed(1)} dtk` : ""}
      </button>
      {open && (
        <div className="mt-1.5 space-y-1.5">
          {tools.map((t, i) => (
            <div key={i} className={`rounded-lg border px-2.5 py-2 text-[10px] ${t.error ? "border-amber-200 bg-amber-50/60" : "border-slate-200 bg-slate-50"}`}>
              <div className="flex items-center gap-1.5 text-slate-500 font-semibold">
                {t.tool === "jalankan_sql" ? <Database className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                {t.tool === "jalankan_sql" ? `SQL · ${t.rows ?? 0} baris` : `Laporan grid-${t.grid_id}`}
                {t.ms != null && <span className="text-slate-300 font-normal">· {t.ms} ms</span>}
              </div>
              {t.sql && <pre className="mt-1 whitespace-pre-wrap break-words font-mono text-[10px] text-slate-600 leading-snug">{t.sql}</pre>}
              {t.error && <p className="mt-1 text-amber-700 flex items-start gap-1"><AlertTriangle className="w-3 h-3 shrink-0 mt-px" />{t.error}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AiMessage({ msg, onAction, disabled }) {
  return (
    <div className="flex gap-2.5 items-start">
      <AiAvatar />
      <div className="flex-1 min-w-0">
        <div className={`rounded-2xl rounded-tl-sm px-3.5 py-2.5 ${msg.error ? "bg-red-50 border border-red-100" : "bg-gray-50"}`}>
          <p className={`text-sm leading-relaxed whitespace-pre-wrap ${msg.error ? "text-red-600" : "text-gray-700"}`}>{msg.text}</p>
        </div>
        <Trace meta={msg.meta} />
        {msg.actions?.length > 0 && (
          <div className="flex gap-1.5 mt-2 flex-wrap">
            {msg.actions.map((a) => (
              <button key={a} onClick={() => onAction(a)} disabled={disabled}
                className="px-2.5 py-1 rounded-full border border-[#14543e]/20 text-[11px] text-[#14543e] font-medium hover:bg-[#e3eae8] disabled:opacity-40 transition-colors text-left">
                {a}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function UserMessage({ msg }) {
  return (
    <div className="flex justify-end">
      <div className="bg-[#14543e] text-white px-3.5 py-2.5 rounded-2xl rounded-tr-sm max-w-[80%]">
        <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.text}</p>
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex gap-2.5 items-start">
      <AiAvatar />
      <div className="bg-gray-50 rounded-2xl rounded-tl-sm px-3.5 py-3">
        <div className="flex gap-1 items-center">
          {[0, 1, 2].map((i) => (
            <div key={i} className="w-1.5 h-1.5 bg-[#14543e] rounded-full animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
          ))}
        </div>
      </div>
    </div>
  );
}

function Welcome({ onPick, disabled }) {
  return (
    <div className="flex gap-2.5 items-start">
      <AiAvatar />
      <div className="flex-1 min-w-0">
        <div className="bg-gray-50 rounded-2xl rounded-tl-sm px-3.5 py-2.5">
          <p className="text-sm text-gray-700 leading-relaxed">
            Halo! Saya membaca data Anda di Landhep: profil bisnis, grid yang dimiliki beserta laporan Analisis Mendalam-nya,
            laporan Skenario, toko di Branch Radar, kota yang dibeli, dan saldo kredit — di semua profil Anda. Tanyakan apa saja seputar itu.
          </p>
        </div>
        <div className="flex gap-1.5 mt-2 flex-wrap">
          {STARTERS.map((a) => (
            <button key={a} onClick={() => onPick(a)} disabled={disabled}
              className="px-2.5 py-1 rounded-full border border-[#14543e]/20 text-[11px] text-[#14543e] font-medium hover:bg-[#e3eae8] disabled:opacity-40 transition-colors text-left">
              {a}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Assistant() {
  const bottomRef = useRef(null);
  const [status, setStatus]       = useState(null);   // null = checking
  const [convs, setConvs]         = useState([]);
  const [activeId, setActiveId]   = useState(null);   // null = new conversation
  const [messages, setMessages]   = useState([]);
  const [loadingConv, setLoadingConv] = useState(false);
  const [input, setInput]         = useState("");
  const [typing, setTyping]       = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // conv id, or "all"
  const [used, setUsed]           = useState(0);

  const refreshStatus = useCallback(() => {
    getAssistantStatus().then((s) => { setStatus(s); setUsed(s?.used_today ?? 0); })
      .catch(() => setStatus({ configured: false }));
  }, []);
  const refreshConvs = useCallback(() => {
    listAssistantConversations().then(setConvs).catch(() => setConvs([]));
  }, []);

  useEffect(() => { refreshStatus(); refreshConvs(); }, [refreshStatus, refreshConvs]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing]);

  const configured = !!status?.configured;
  const enabled = status?.enabled !== false;
  const cap = status?.daily_cap ?? 0;
  const quotaOut = cap > 0 && used >= cap;
  const canSend = configured && enabled && !quotaOut && !typing;

  const openConversation = async (id) => {
    if (id === activeId) return;
    setActiveId(id);
    setMessages([]);
    setLoadingConv(true);
    try {
      const c = await getAssistantConversation(id);
      setMessages(c.messages || []);
    } catch (e) {
      setMessages([{ id: "err", role: "ai", error: true, text: e.message }]);
    } finally {
      setLoadingConv(false);
    }
  };

  const newConversation = () => { setActiveId(null); setMessages([]); setConfirmDel(null); };

  const sendMessage = async (text) => {
    const t = (text || "").trim();
    if (!t || !canSend) return;
    const tempId = `u-${Date.now()}`;
    setMessages((m) => [...m, { id: tempId, role: "user", text: t }]);
    setInput("");
    setTyping(true);
    try {
      const res = await postAssistantChat({ message: t, conversationId: activeId });
      if (res?.configured === false) { setStatus((s) => ({ ...(s || {}), configured: false })); return; }
      setMessages((m) => [...m, { id: res.id, role: "ai", text: res.text, actions: res.actions, meta: res.meta }]);
      setUsed(res.usedToday ?? used + 1);
      if (!activeId) { setActiveId(res.conversationId); }
      refreshConvs();
    } catch (e) {
      setMessages((m) => [...m, { id: `e-${Date.now()}`, role: "ai", error: true, text: e.message || "Asisten AI sedang tidak bisa dihubungi." }]);
    } finally {
      setTyping(false);
    }
  };

  const doDelete = async () => {
    const target = confirmDel;
    setConfirmDel(null);
    try {
      if (target === "all") {
        await deleteAllAssistantConversations();
        newConversation();
      } else {
        await deleteAssistantConversation(target);
        if (target === activeId) newConversation();
      }
      refreshConvs();
    } catch (e) {
      alert(`Gagal menghapus: ${e.message}`);
    }
  };

  return (
    <div className="relative h-full">
      <div className="absolute inset-0 overflow-hidden" style={{ paddingTop: "84px", paddingLeft: "108px" }}>
        <div className="h-full grid grid-cols-[280px_1fr] gap-3 p-3">

          {/* ── Conversations ── */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col overflow-hidden">
            <div className="px-3 py-3 border-b border-gray-100 shrink-0">
              <button onClick={newConversation} disabled={!configured}
                className="w-full bg-[#14543e] hover:bg-[#104230] disabled:opacity-40 text-white py-2 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-colors">
                <Plus className="w-3.5 h-3.5" /> Percakapan baru
              </button>
            </div>
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 shrink-0">
              <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Riwayat</span>
              {convs.length > 0 && (
                confirmDel === "all" ? (
                  <span className="flex items-center gap-1.5 text-[10px]">
                    <button onClick={doDelete} className="font-bold text-red-600 hover:underline">Hapus semua</button>
                    <button onClick={() => setConfirmDel(null)} className="text-slate-400 hover:underline">Batal</button>
                  </span>
                ) : (
                  <button onClick={() => setConfirmDel("all")} className="text-[10px] font-semibold text-slate-400 hover:text-red-600 transition-colors">Hapus semua</button>
                )
              )}
            </div>
            <div className="flex-1 overflow-y-auto scrollbar-hide">
              {convs.length === 0 ? (
                <div className="flex flex-col items-center justify-center px-5 py-8 text-center">
                  <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center mb-3">
                    <MessageSquare className="w-5 h-5 text-slate-300" />
                  </div>
                  <p className="text-xs font-semibold text-slate-400">Belum ada percakapan</p>
                  <p className="text-[10px] text-slate-300 mt-1 leading-relaxed">Percakapan Anda tersimpan di sini dan hanya Anda yang bisa melihat atau menghapusnya.</p>
                </div>
              ) : convs.map((c) => {
                const on = c.id === activeId;
                return (
                  <div key={c.id}
                    className={`group flex items-start gap-2 px-3 py-2.5 border-b border-slate-50 transition-colors ${on ? "bg-[#e3eae8]" : "hover:bg-slate-50"}`}>
                    <button onClick={() => openConversation(c.id)} className="flex-1 min-w-0 text-left">
                      <p className={`text-[12px] font-semibold truncate ${on ? "text-[#14543e]" : "text-slate-700"}`}>{c.title || "Percakapan"}</p>
                      <p className="text-[10px] text-slate-400">{fmtTime(c.updatedAt)} · {c.messageCount} pesan</p>
                    </button>
                    {confirmDel === c.id ? (
                      <span className="flex items-center gap-1 text-[10px] shrink-0 pt-0.5">
                        <button onClick={doDelete} className="font-bold text-red-600 hover:underline">Hapus</button>
                        <button onClick={() => setConfirmDel(null)} className="text-slate-400 hover:underline">Batal</button>
                      </span>
                    ) : (
                      <button onClick={() => setConfirmDel(c.id)} title="Hapus percakapan"
                        className="shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-slate-300 hover:text-red-600 hover:bg-white opacity-0 group-hover:opacity-100 transition-all">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Chat ── */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-[#14543e] flex items-center justify-center">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800">Asisten Strategi</p>
                  <p className={`text-[9px] font-semibold uppercase tracking-widest ${configured && enabled ? "text-emerald-500" : "text-slate-400"}`}>
                    {status == null ? "Memeriksa…" : !configured ? "Belum dikonfigurasi" : !enabled ? "Dinonaktifkan" : "Aktif · menjawab dari data Anda"}
                  </p>
                </div>
              </div>
              {configured && cap > 0 && (
                <span className={`text-[10px] font-semibold ${quotaOut ? "text-red-500" : "text-slate-400"}`}>
                  {used}/{cap} pesan hari ini
                </span>
              )}
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3.5 scrollbar-hide">
              {!configured && status != null ? (
                <div className="h-full flex flex-col items-center justify-center text-center px-6">
                  <div className="w-12 h-12 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center mb-3">
                    <KeyRound className="w-5 h-5 text-amber-500" />
                  </div>
                  <p className="text-sm font-bold text-slate-700">Asisten AI belum dikonfigurasi</p>
                  <p className="text-xs text-slate-400 mt-1 max-w-sm leading-relaxed">Server belum memiliki kunci Gemini untuk asisten. Hubungi admin Landhep untuk mengaktifkannya.</p>
                </div>
              ) : (
                <>
                  {loadingConv && <p className="text-xs text-slate-400">Memuat percakapan…</p>}
                  {!loadingConv && messages.length === 0 && <Welcome onPick={sendMessage} disabled={!canSend} />}
                  {messages.map((msg) =>
                    msg.role === "ai"
                      ? <AiMessage key={msg.id} msg={msg} onAction={sendMessage} disabled={!canSend} />
                      : <UserMessage key={msg.id} msg={msg} />
                  )}
                  {typing && <TypingIndicator />}
                </>
              )}
              <div ref={bottomRef} />
            </div>

            <div className="px-3 py-3 border-t border-gray-100 shrink-0">
              <form onSubmit={(e) => { e.preventDefault(); sendMessage(input); }}
                className="flex items-center gap-2 bg-gray-50 border border-gray-100 rounded-xl px-3 py-2">
                <input type="text" value={input} onChange={(e) => setInput(e.target.value)} maxLength={2000}
                  disabled={!configured || !enabled || quotaOut}
                  placeholder={quotaOut ? "Kuota harian habis — coba lagi besok" : "Tanyakan tentang lokasi, kompetitor, toko, atau saldo Anda…"}
                  className="flex-1 bg-transparent text-sm text-gray-700 placeholder-gray-300 focus:outline-none disabled:cursor-not-allowed" />
                <button type="submit" disabled={!input.trim() || !canSend}
                  className="w-7 h-7 bg-[#14543e] hover:bg-[#104230] disabled:opacity-40 rounded-lg flex items-center justify-center transition-colors shrink-0">
                  <Send className="w-3 h-3 text-white" />
                </button>
              </form>
              <p className="text-[10px] text-slate-300 mt-1.5 px-1">
                Jawaban AI dibaca dari data tersimpan Anda dan bisa keliru — verifikasi sebelum memutuskan. Percakapan tersimpan dan hanya Anda yang bisa menghapusnya.
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
